import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";
import AuthBackground from "../components/AuthBackground";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [googleError, setGoogleError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  // Clear localStorage when tab is closed
  useCleanupOnUnload();
  console.log(googleLoading);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate("/discovery");
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (credential) => {
      if (!credential) return;
      setGoogleError("");
      setGoogleLoading(true);
      const result = await googleLogin(credential);
      if (result.success) {
        navigate("/discovery");
      } else {
        setGoogleError(result.message);
      }
      setGoogleLoading(false);
    },
    [googleLogin, navigate],
  );

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // Wait for Google script to load
    const checkGoogleLoaded = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => handleGoogleCredential(response.credential),
        });

        const buttonElement = document.getElementById("google-signin-button");
        if (buttonElement) {
          window.google.accounts.id.renderButton(buttonElement, {
            theme: "outline",
            size: "large",
            shape: "pill",
            width: 280,
          });
          setGoogleButtonReady(true);
        }
      } else {
        // Google not loaded yet, try again
        setTimeout(checkGoogleLoaded, 100);
      }
    };

    // Add a timeout to ensure we don't wait forever
    const timeout = setTimeout(checkGoogleLoaded, 500);
    return () => clearTimeout(timeout);
  }, [handleGoogleCredential]);

  // Check if there are any error messages or additional content that might cause overflow

  const availableHeightExpression =
    "calc(var(--auth-vh, 1vh) * 100 - var(--auth-nav-height, 4rem) - var(--auth-footer-height, 3rem))";

  const contentStyle = useMemo(
    () => ({
      paddingTop: "calc(var(--auth-nav-height, 4rem) + 1.5rem)",
      paddingBottom: "calc(var(--auth-footer-height, 3rem) + 1.5rem)",
      minHeight: `max(70vh, ${availableHeightExpression})`,
      width: "100%",
    }),
    [availableHeightExpression],
  );

  const cardStyle = useMemo(
    () => ({
      boxShadow: `
        0 10px 25px -8px rgba(139, 92, 246, 0.15),
        0 0 0 1px rgba(255, 255, 255, 0.5),
        0 0 20px rgba(168, 85, 247, 0.2)
      `,
      maxHeight: `calc(${availableHeightExpression} - 2rem)`,
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    }),
    [availableHeightExpression],
  );

  return (
    <AuthBackground>
      <Navbar />

      {/* Main content - fills available space between navbar and footer */}
      <div
        className="flex-1 flex items-center justify-center px-3 sm:px-4 md:px-5 py-6 relative"
        style={contentStyle}
      >
        {/* Login Card - with static glow effect (animation removed) */}
        <div
          className="
            bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl md:rounded-3xl 
            p-4 sm:p-5 md:p-7 lg:p-9 
            w-[95%] sm:w-[90%] md:w-full max-w-sm sm:max-w-md 
            transition-all duration-500 ease-out
          "
          style={cardStyle}
        >
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 sm:mb-2 text-center">
            Welcome Back
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-500 mb-4 sm:mb-6 md:mb-8 text-center">
            Sign in to continue your journey
          </p>

          {/* Error Messages - responsive */}
          {error && (
            <div className="mb-3 sm:mb-4 md:mb-5 rounded-lg border border-red-200 bg-red-50/90 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 text-xs sm:text-sm text-red-600 backdrop-blur-sm">
              {error}
            </div>
          )}

          {googleError && (
            <div className="mb-3 sm:mb-4 md:mb-5 rounded-lg border border-red-200 bg-red-50/90 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 text-xs sm:text-sm text-red-600 backdrop-blur-sm">
              {googleError}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 sm:gap-4 md:gap-5"
          >
            <div className="flex flex-col">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-lg sm:rounded-xl border border-gray-200 bg-white/80 px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base focus:border-purple-400 focus:outline-none focus:ring-4 focus:ring-purple-100/70 focus:bg-white transition-all"
              />
            </div>

            <div className="flex flex-col">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-lg sm:rounded-xl border border-gray-200 bg-white/80 px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base focus:border-purple-400 focus:outline-none focus:ring-4 focus:ring-purple-100/70 focus:bg-white transition-all"
              />
              <div className="text-right mt-1 sm:mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs sm:text-sm text-purple-600 font-semibold hover:text-purple-700 hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-purple-200/70 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="my-3 sm:my-4 md:my-5 flex items-center gap-2 sm:gap-3">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1" />
            <span className="text-xs sm:text-sm text-gray-400">or</span>
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1" />
          </div>

          <div className="flex justify-center min-h-[40px] sm:min-h-[45px] md:min-h-[50px]">
            {!googleButtonReady && (
              <div className="flex items-center justify-center text-xs sm:text-sm text-gray-400">
                Loading Google sign in...
              </div>
            )}
            <div
              id="google-signin-button"
              className={`w-full flex justify-center ${!googleButtonReady ? "hidden" : ""}`}
            />
          </div>

          {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-400 text-center">
              Google login is not configured.
            </div>
          )}

          <p className="text-center mt-3 sm:mt-4 md:mt-5 text-xs sm:text-sm text-gray-500">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-purple-600 font-semibold hover:text-purple-700 hover:underline transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Footer - responsive */}
      <Footer />
    </AuthBackground>
  );
}

export default Login;
